import axios, {AxiosInstance, AxiosResponse} from 'axios';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8080';

class HttpClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.authInterceptor();

    // Request interceptor to add language header
    this.languageInterceptor();

    // Response interceptor to handle token refresh and errors
    this.tokenRefreshInterceptor();
  }

  private tokenRefreshInterceptor() {
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // Check for refreshed token in response headers
        const newToken = response.headers.authorization;
        if (newToken && newToken.startsWith('Bearer ')) {
          const token = newToken.substring('Bearer '.length);
          localStorage.setItem('authToken', token);
        }
        return response;
      },
      (error) => {
        if (error.response?.status === 403 || error.response?.status === 401) {
          // Clear token and redirect to login
          localStorage.removeItem('authToken');
          // window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  private authInterceptor() {
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  private languageInterceptor() {
    this.client.interceptors.request.use(
      (config) => {
        const language = localStorage.getItem('language');
        if (language) {
          config.headers['Accept-Language'] = language;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  public get<T>(url: string): Promise<T> {
    return this.client.get(url).then(response => response.data);
  }

  public post<T>(url: string, data?: any): Promise<T> {
    return this.client.post(url, data).then(response => response.data);
  }

  public put<T>(url: string, data?: any): Promise<T> {
    return this.client.put(url, data).then(response => response.data);
  }

  public delete<T>(url: string): Promise<T> {
    return this.client.delete(url).then(response => response.data);
  }

  public postFormData<T>(url: string, formData: FormData): Promise<T> {
    return this.client.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => response.data);
  }

  // Special method for login that doesn't include auth token
  public loginRequest<T>(url: string, data: any): Promise<T> {
    return axios.post(`${API_BASE_URL}${url}`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(response => response.data);
  }
}

const httpClient = new HttpClient();
export default httpClient;
